import { FunctionComponent } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    DialogActions,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Formik } from 'formik';
import { FlexContainer, SectionContainer } from '@/components/Layout/Container';
import { BoldLabel, RequiredMark } from '@/components/Layout/Textual';
import { ContingentCondition } from '@/models/product';
import { FormikTextField } from '@/components/Layout/Form/FomikTextField';
import { DatePickerField } from '@/components/Layout/Form/DatePickerField';
import { WeekdaySelectField } from '@/components/Layout/Form/WeekdaySelectField';
import * as Yup from 'yup';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetProductPersonNameConfigurationQuery } from '@/api/v2/textReplacement';
import { LoadingButton } from '@mui/lab';
import {
    ConditionFormCardContent,
    Hint,
    Row,
    RowInfo,
    SectionTitle,
    TextInputContainer,
    TitleCardContent,
} from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ConditionFormComponents';
import { AutocompleteField } from '@/components/Layout/Form/AutoCompleteField';
import {
    ConfigurationIdNameMap,
    ProductPersonNameConfiguration,
} from '@/models/textReplacement';
import { FormHeader } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ConditionList';
import { ProductEnvironmentField } from './ProductEnvironmentField';

const ContingentFormSchema = Yup.object().shape({
    name: Yup.string().required(
        'Bitte geben Sie einen Namen für die Regel ein'
    ),
    maxTicketsPerStartDay: Yup.number()
        .required('Bitte geben Sie eine Anzahl an Ticket an')
        .min(0, 'Anzahl an Tickets muss mindestens 0 sein'),
});

type ContingentConditionFormProps = {
    condition: ContingentCondition;
    onSave: (condition: ContingentCondition) => void;
    onClose: () => void;
    isNewCondition: boolean;
    isLoading?: boolean;
};

export const ContingentConditionForm: FunctionComponent<
    React.PropsWithChildren<ContingentConditionFormProps>
> = ({ condition, onClose, onSave, isLoading, isNewCondition }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data } = useGetProductPersonNameConfigurationQuery(liftOperatorId);

    return (
        <Box
            sx={{
                width: '860px',
            }}
        >
            <FormHeader
                title={
                    !isNewCondition
                        ? `Regel "${condition.name}" bearbeiten`
                        : 'Neue Regel anlegen'
                }
                subtitle={
                    <>
                        Alle Felder sind optional befüllbar (Ausnahme: „Interne
                        Bezeichnung“).
                        <br />
                        Nur befüllte Felder kommen zur Anwendung und übersteuern
                        die Basiskonfiguration des Tickets, leere Felder werden
                        ignoriert.
                        <br />
                        Es kommt jeweils nur die erste Regel aus jeder Kategorie
                        („Verfügbarkeit“ / „Preis“) zur Anwendung, die auf die
                        Suchanfrage des Gastes passt.
                        <br />
                        Die Abfrage der Regeln erfolgt nach der
                        Speicheranordnung (Priorität), von oben nach unten.
                    </>
                }
            />
            <Formik
                validationSchema={ContingentFormSchema}
                validateOnChange={false}
                initialValues={condition}
                onSubmit={(values: ContingentCondition) => {
                    // ensure if values are empty that they are null
                    onSave({
                        ...values,
                        sellingGroup: values.sellingGroup || null,
                        notSellingGroup: values.notSellingGroup || null,
                    });
                }}
            >
                {({ errors, values, setFieldValue }) => (
                    <form onSubmit={(e) => e.preventDefault()}>
                        <Stack
                            sx={{
                                p: 6,
                            }}
                            spacing={6}
                        >
                            <Card>
                                <TitleCardContent>
                                    <Typography variant='h6'>
                                        1. Regel
                                    </Typography>
                                </TitleCardContent>

                                <Divider />
                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <TextInputContainer>
                                            <FormikTextField
                                                name='name'
                                                textFieldProps={{
                                                    error: !!errors['name'],
                                                    helperText:
                                                        errors['name'] || '',
                                                    label: (
                                                        <span>
                                                            Interne Bezeichnung{' '}
                                                            <RequiredMark />
                                                        </span>
                                                    ),
                                                }}
                                            />
                                        </TextInputContainer>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.de'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName'
                                                        ]?.['de'],
                                                        helperText:
                                                            errors[
                                                                'externalName'
                                                            ]?.['de'] || '',
                                                        label: 'Anzeigename Deutsch',
                                                    }}
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='externalName.en'
                                                    textFieldProps={{
                                                        error: !!errors[
                                                            'externalName.en'
                                                        ],
                                                        helperText:
                                                            errors[
                                                                'externalName.en'
                                                            ] || '',
                                                        label: 'Anzeigename English',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        2. Bedingung&nbsp;
                                    </Typography>
                                    <Typography>
                                        - wann kommt die Regel zur Anwendung
                                    </Typography>
                                </TitleCardContent>

                                <Divider />

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Für Verkäufe bei starjack.hospitality:
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Row>
                                        <TextInputContainer>
                                            <FormikTextField
                                                name='sellingGroup'
                                                textFieldProps={{
                                                    error: !!errors[
                                                        'sellingGroup'
                                                    ],
                                                    helperText:
                                                        errors[
                                                            'sellingGroup'
                                                        ] || '',
                                                    label: 'hospitality-Verkaufsgruppe einschließen',
                                                }}
                                            />
                                        </TextInputContainer>

                                        <TextInputContainer>
                                            <FormikTextField
                                                name='notSellingGroup'
                                                textFieldProps={{
                                                    error: !!errors[
                                                        'notSellingGroup'
                                                    ],
                                                    helperText:
                                                        errors[
                                                            'notSellingGroup'
                                                        ] || '',

                                                    label: 'hospitality-Verkaufsgruppe ausschließen',
                                                }}
                                            />
                                        </TextInputContainer>
                                    </Row>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket-Gültigkeit (Startdatum)
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Hinweis für{' '}
                                            {renderSeasonTicketsBoldLabel(':')}{' '}
                                            Konfigurationen der Bedingung
                                            „Ticket-Gültigkeit“ werden erst am
                                            hinterlegten Datum im Onlineverkauf
                                            sichtbar (gleich wie bei
                                            „Ticket-Verkaufszeitraum“), da für
                                            diese Tickets im Shop kein
                                            Startdatum auswählbar ist.
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <FormikTextField
                                                    name='minDaysBeforeTicketStart'
                                                    textFieldProps={{
                                                        type: 'number',
                                                        label: 'Tage vor dem Ticket-Startdatum',
                                                    }}
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row alignItems='center'>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidFrom'
                                                    label='Gültig ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name='startDateValidTo'
                                                    label='Gültig bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Row>
                                            <Stack>
                                                <Typography
                                                    sx={{
                                                        mb: 2,
                                                    }}
                                                >
                                                    In diesem Zeitraum nur
                                                    gültig an den Wochentagen:
                                                </Typography>
                                                <WeekdaySelectField
                                                    name={
                                                        'startDateValidWeekdays'
                                                    }
                                                />
                                            </Stack>
                                        </Row>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography
                                        sx={{
                                            fontWeight: 500,
                                        }}
                                    >
                                        Ticket Verkaufszeitraum&nbsp;
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wenn der Verkaufszeitraum in der
                                            Zukunft liegt, ist die Regel auch
                                            erst im hinterlegten
                                            Verkaufszeitraum im Shop sichtbar!
                                        </Hint>

                                        <Row>
                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validFrom'}
                                                    label='Verkaufszeitraum ab'
                                                />
                                            </TextInputContainer>

                                            <TextInputContainer>
                                                <DatePickerField
                                                    name={'validTo'}
                                                    label='Verkaufszeitraum bis'
                                                />
                                            </TextInputContainer>
                                        </Row>

                                        <Stack>
                                            <RowInfo>
                                                In diesem Zeitraum nur gültig an
                                                den Wochentagen:
                                            </RowInfo>
                                            <WeekdaySelectField
                                                name={'validWeekdays'}
                                            />
                                        </Stack>
                                    </Stack>
                                </ConditionFormCardContent>

                                <SectionTitle>
                                    <Typography variant={'subtitle2'}>
                                        Für folgende Personentypen
                                    </Typography>
                                </SectionTitle>

                                <ConditionFormCardContent>
                                    <Stack spacing={4}>
                                        <Hint>
                                            Wähle hier all jene
                                            Personenkategorien, für welche die
                                            Regel zur Anwendung kommen soll.
                                            Hier gelistet sind die
                                            Originalbeschreibungen, wie sie von
                                            SKIDATA an starjack übermittelt
                                            werden:
                                        </Hint>

                                        <Row>
                                            <AutocompleteField
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiAutocomplete-tag': {
                                                        mx: 1,
                                                        my: 2,
                                                    },
                                                }}
                                                name='personTypeIds'
                                                options={data?.persons || []}
                                                getOptionLabel={(
                                                    option: ConfigurationIdNameMap
                                                ) => option.name}
                                                onChange={(options) => {
                                                    setFieldValue(
                                                        'personTypeIds',
                                                        options.map((o) => o.id)
                                                    );
                                                }}
                                                valueTransformHandler={(
                                                    values: string[]
                                                ) => {
                                                    if (data) {
                                                        const { persons } =
                                                            data as ProductPersonNameConfiguration;
                                                        return persons.filter(
                                                            (p) =>
                                                                values.includes(
                                                                    p.id
                                                                )
                                                        );
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label='Personentypen auswählen'
                                                    />
                                                )}
                                                renderTags={(
                                                    value: ConfigurationIdNameMap[],
                                                    getTagProps
                                                ) =>
                                                    value.map(
                                                        (
                                                            option: ConfigurationIdNameMap,
                                                            index: number
                                                        ) => (
                                                            <Chip
                                                                key={index}
                                                                color='primary'
                                                                label={
                                                                    option.name
                                                                }
                                                                {...getTagProps(
                                                                    {
                                                                        index,
                                                                    }
                                                                )}
                                                            />
                                                        )
                                                    )
                                                }
                                            />
                                        </Row>

                                        <Stack>
                                            <RowInfo>
                                                Für folgende Umgebung
                                                freischalten:
                                            </RowInfo>
                                            <ProductEnvironmentField
                                                values={values['environments']}
                                                onChange={(environments) => {
                                                    setFieldValue(
                                                        'environments',
                                                        environments
                                                    );
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </ConditionFormCardContent>
                            </Card>

                            <Card>
                                <TitleCardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant='h6'>
                                        3. Aktion&nbsp;
                                    </Typography>
                                    <Typography>- daraus resultiert</Typography>
                                </TitleCardContent>
                                <Divider />

                                <ConditionFormCardContent>
                                    <Row>
                                        <TextInputContainer>
                                            <FormikTextField
                                                name='maxTicketsPerStartDay'
                                                textFieldProps={{
                                                    type: 'number',
                                                    label: (
                                                        <span>
                                                            Anzahl an Tickets
                                                            <RequiredMark />
                                                        </span>
                                                    ),
                                                }}
                                            />
                                        </TextInputContainer>
                                    </Row>
                                </ConditionFormCardContent>
                            </Card>

                            {!!Object.keys(errors).length && (
                                <SectionContainer>
                                    <FlexContainer>
                                        <Typography>
                                            Bitte füllen Sie zuerst alle
                                            Pflichtfelder aus
                                        </Typography>
                                    </FlexContainer>
                                </SectionContainer>
                            )}

                            <DialogActions>
                                <Button onClick={onClose} color='primary'>
                                    Schließen
                                </Button>

                                <LoadingButton
                                    type='submit'
                                    color='primary'
                                    variant='contained'
                                    loading={isLoading}
                                >
                                    Speichern
                                </LoadingButton>
                            </DialogActions>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

const renderSeasonTicketsBoldLabel = (addOn = '') => (
    <BoldLabel
        sx={{
            fontSize: '0.85rem',
        }}
    >
        Saison-/Jahres- oder Depotkarten{addOn}
    </BoldLabel>
);
